// Whether the camera is allowed to move on its own, and how far that goes.
//
// The board grew and the camera learned to lead: an opponent's move several
// screens away resolved entirely off screen, so the drivers borrow the view,
// show you the thing, and hand it back. That is right on a board that runs
// past the viewport and wrong for anyone who wants to keep looking at their
// own factory — and it was not a choice anybody could make.
//
// Two toggles, because there are two separable complaints:
//
//  - **Auto camera.** Off means the view is yours and nothing but your own
//    drag ever moves it. Every borrowed look is dropped, including the one
//    that shows you a new front arriving.
//  - **Follow the piece.** On its own, "auto camera" would still chase every
//    single enemy move, which is the part that reads as the board being flown
//    rather than played. Off, the camera still goes to the rare, large things
//    — new ground, a force landing — and leaves the move-by-move alone.
//
// **Never on a standard board**, whatever these say. Camera offsets exist to
// reach ground that is off screen; on the classic eight-by-eight there is no
// such ground, so a lead is a shove and a hand-back is a second one. That is a
// property of the board rather than a preference, so it is enforced at the
// call site (`GameShell`) rather than being a third toggle.

import { useCallback, useState } from "react";

import type { Tilt } from "src/client/ai/render/cameraRig";
import { DEFAULT_SPEED, snapSpeed } from "src/client/ai/render/tempo";

export interface CameraPrefs {
  /** Let anything but your own drag move the view. */
  autoFocus: boolean;
  /**
   * Chase the piece that just moved, as against only board-sized events.
   *
   * **On**, by the designer's call (2026-09-17): a new player on a board wider
   * than the screen should be shown the move they cannot see. It was off for
   * a while — two or three unrequested camera moves a turn was the complaint —
   * and what answers the complaint now is that `look` leaves a move already on
   * screen alone, and that off really is off. With it off the edge marker
   * takes over: the unseen move gets a mark at the edge of the free area, and
   * going there is then a click *you* made.
   */
  trackPiece: boolean;
  /**
   * Looking across the board or straight down onto it.
   *
   * **A preference rather than a gesture**, and global rather than per side:
   * pitch used to be the other axis of the orbit drag, so every wobbly click
   * left the board at an angle nobody chose and nothing snapped it back. Two
   * presets, a tile and a key. See `CameraRig.setTilt`.
   */
  tilt: Tilt;
  /**
   * Draw with a perspective camera instead of the orthographic one.
   *
   * **On**, by the designer's call (2026-09-17): a board that reads as a place
   * rather than as a diagram is the game's look. The orthographic camera stays
   * on offer for the argument that used to win — every square the same size on
   * screen is why a chessboard reads at a glance, and a lane, a heap and a
   * piece share a square here — because the trade is taste, not correctness.
   *
   * Unlike the two above it is **not** silenced on the classic board. Those
   * are about reaching ground that is off screen, which an eight-by-eight has
   * none of; this is about what the board looks like, and an eight-by-eight
   * has that.
   */
  perspective: boolean;
  /**
   * Stop the opponent's turn being dealt out one command at a time.
   *
   * **Not a camera setting, and neither is `perspective`.** This bag is what
   * the View menu remembers; the name is where it started, and the storage key
   * is load-bearing (renaming it drops everybody's stored settings), so it
   * keeps it. What the menu is for is "how the board plays back to me", and
   * both of these are that.
   *
   * What it relaxes is the *ordering*, not the speed. `render/tempo.ts` already
   * plays somebody else's turn at up to three times speed; this says the
   * animations do not queue at all — the whole turn is dispatched together and
   * the factory's beat runs over the move instead of behind it. Two places
   * read it, because there are two queues: `useSoloOpponent` deals the
   * commands, and `BoardRenderer` stages the factory behind the pieces.
   */
  opponentFast: boolean;
  /**
   * How fast the board animates, as a multiplier over every playback tempo.
   *
   * **The one speed control that is about the player rather than about the
   * game.** `render/tempo.ts` decides how fast somebody else's turn plays by
   * how crowded the board is and the watch transport says how fast a game
   * nobody is playing runs — both are facts about the situation. This is a person saying how fast they
   * like watching, so it applies always, in every mode, and it is the only one
   * that scales the player's own turn.
   *
   * A number off the `SPEEDS` ladder, snapped on the way in, so the control can
   * never show a speed the board is not playing at.
   *
   * `GameShell` folds it into the single number both clocks take — the driver
   * deals commands at `STEP_MS / pace` and the renderer plays at that same
   * scale — because a speed reaching one of them is half a control: the pieces
   * finish and wait, or the next turn's commands land on a board still moving.
   */
  animationSpeed: number;
}

/**
 * What the game opens on.
 *
 * A preference that changes what somebody is used to should default to what
 * they are used to; the toggle is for the people it was wrong for. **Both
 * camera toggles and `perspective` are on** — see their own notes. A stored
 * setting wins over all of it, so nobody's existing choice moves.
 */
export const DEFAULT_CAMERA_PREFS: CameraPrefs = {
  autoFocus: true,
  trackPiece: true,
  // Across the table, which is the pitch the board was designed and measured
  // at — see the occlusion note in `docs/claude/interface/picking-and-camera.md`.
  tilt: "table",
  perspective: true,
  // Off: a preference that changes what somebody is used to defaults to what
  // they are used to, and the
  // staging it switches off is there on purpose — a turn should read as a
  // sequence of things that happened. The toggle is for the people it is too
  // slow for.
  opponentFast: false,
  // Ordinary speed, for the reason above: a preference that changes what
  // somebody is used to defaults to what they are used to.
  animationSpeed: DEFAULT_SPEED
};

const KEY = "chessfactory.camera";

/**
 * Read the stored preference, falling back to the default on anything at all.
 *
 * Anything: no storage (a private window refuses it and *throws* on access),
 * a value from an older shape, a half-written string. A camera setting is not
 * worth an exception on first paint.
 */
export function loadCameraPrefs(): CameraPrefs {
  try {
    const raw = window.localStorage?.getItem(KEY);
    if (!raw) return DEFAULT_CAMERA_PREFS;
    const parsed = JSON.parse(raw) as Partial<CameraPrefs>;
    return {
      autoFocus: parsed.autoFocus ?? DEFAULT_CAMERA_PREFS.autoFocus,
      trackPiece: parsed.trackPiece ?? DEFAULT_CAMERA_PREFS.trackPiece,
      // Named rather than `??`'d, for the reason the speed is snapped: this
      // one is not a boolean, so absent is not its only wrong shape — and a
      // string off the pair reaches `TILT_PITCH` as a miss, which is a camera
      // pitch of `undefined` and a board that never draws again.
      tilt: parsed.tilt === "top" ? "top" : DEFAULT_CAMERA_PREFS.tilt,
      // Absent from every setting stored before the toggle existed, which is
      // the case the `??` chain is for: an older shape has to read as the
      // default rather than as `undefined` reaching the renderer.
      perspective: parsed.perspective ?? DEFAULT_CAMERA_PREFS.perspective,
      opponentFast: parsed.opponentFast ?? DEFAULT_CAMERA_PREFS.opponentFast,
      // **Snapped rather than defaulted, because this one is a number.** Every
      // key above is a boolean, where absent is the only wrong shape there is;
      // a speed can also arrive as a string, as `0` from a hand-edited store,
      // or as a value from a ladder that has since changed — and a nought is
      // the one that matters, since the driver paces itself at `STEP_MS / pace`
      // and would never advance a turn again.
      animationSpeed: snapSpeed(parsed.animationSpeed)
    };
  } catch {
    return DEFAULT_CAMERA_PREFS;
  }
}

function saveCameraPrefs(prefs: CameraPrefs): void {
  try {
    window.localStorage?.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // A setting that cannot be remembered still has to work for this session.
  }
}

/**
 * What the View menu remembers, held in state and kept across games.
 *
 * `set` takes a patch rather than a whole object so a caller flipping one
 * toggle cannot silently reset the other — which is exactly the bug a menu
 * item with two checkboxes invites.
 */
export function useCameraPrefs(): [CameraPrefs, (patch: Partial<CameraPrefs>) => void] {
  const [prefs, setPrefs] = useState<CameraPrefs>(loadCameraPrefs);
  const set = useCallback((patch: Partial<CameraPrefs>) => {
    setPrefs((was) => {
      const next = { ...was, ...patch };
      saveCameraPrefs(next);
      return next;
    });
  }, []);
  return [prefs, set];
}
